import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { ACTIVE_DESTINATIONS } from "@/lib/destinations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SiteContentCtx = createContext(null);

// Guard against divergence between frontend destinations.js and backend ACTIVE_COUNTRIES.
function checkDestinationsConsistency() {
  axios
    .get(`${API}/config/destinations`)
    .then((r) => {
      const backend = [...(r.data?.active || [])].sort().join(",");
      const frontend = [...ACTIVE_DESTINATIONS].sort().join(",");
      if (backend !== frontend) {
        console.warn(
          `[destinations] Mismatch: frontend active: [${frontend}] vs backend ACTIVE_COUNTRIES: [${backend}]. Align lib/destinations.js and backend/.env.`
        );
      }
    })
    .catch(() => {});
}

/**
* SiteContentProvider: fetches the resolved content for current persona/country
 * and exposes a `t(sectionKey, fieldKey, fallback)` helper. Components can
 * always fall back to their hardcoded copy if the CMS is unreachable.
 *
 * The provider re-fetches when persona or country changes (consumers pass
* those via the PersonaContext below it in the tree, but to keep this
 * provider self-contained we read them from localStorage too).
 */
export function SiteContentProvider({ children }) {
  const [sections, setSections] = useState({});
  const [persona, setPersona] = useState("default");
  const [country, setCountry] = useState("default");

  // Detect persona/country from localStorage (keeps in sync with PersonaContext)
  useEffect(() => {
    const read = () => {
      try {
        const raw = localStorage.getItem("ma_persona_v1");
        if (!raw) return { persona: "default", country: "default" };
        const p = JSON.parse(raw);
        const persona = p.persona === "retire" || p.persona === "relocate" ? p.persona : "default";
        const country =
          p.country === "Spain" || p.country === "Portugal" || p.country === "Italy"
            ? p.country
            : "default";
        return { persona, country };
      } catch {
        return { persona: "default", country: "default" };
      }
    };
    const next = read();
    setPersona(next.persona);
    setCountry(next.country);
    // Listen for cross-tab/local updates
    const onStorage = (e) => {
      if (e.key === "ma_persona_v1") {
        const n = read();
        setPersona(n.persona);
        setCountry(n.country);
      }
    };
    window.addEventListener("storage", onStorage);
    // Poll for in-tab changes (PersonaContext writes synchronously, this catches it)
    const id = setInterval(() => {
      const n = read();
      setPersona((prev) => (prev !== n.persona ? n.persona : prev));
      setCountry((prev) => (prev !== n.country ? n.country : prev));
    }, 1200);
    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    checkDestinationsConsistency();
  }, []);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/site-content`, { params: { persona, country } })
      .then((r) => mounted && setSections(r.data?.sections || {}))
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [persona, country]);

  const t = (sectionKey, fieldKey, fallback = "") => {
    const v = sections?.[sectionKey]?.[fieldKey];
    if (v === undefined || v === null || v === "") return fallback;
    return v;
  };

  // section(sectionKey, fallbackObj): returns the whole resolved section (merged with fallback)
  const section = (sectionKey, fallbackObj = {}) => {
    const live = sections?.[sectionKey] || {};
    return { ...fallbackObj, ...live };
  };

  return (
    <SiteContentCtx.Provider value={{ t, section, persona, country, sections }}>
      {children}
    </SiteContentCtx.Provider>
  );
}

export function useSiteContent() {
  return useContext(SiteContentCtx) || { t: (_s, _f, fb = "") => fb, section: (_s, fb = {}) => fb, sections: {} };
}
